import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { FileBox, MCButton, MCError, MCText, View } from 'src/components/basic';
import MCBaseEditableTable, { MCEditText } from 'src/components/complex/MCBaseEditableTable';
import { remCss, scssVariables, useScreenDetail } from 'src/helpers';
import { MediaGroup, popupModel } from 'src/components/complex';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import { AddGallery } from './Add';
import model from './model';
import LANG_CONST from 'src/helpers/lang/const';
import { EditMedia } from './EditMedia';

const openMediaEdit = (item, gallery, isMain = false) => {
  popupModel.setActivePopup('slide_media', { active: false });
  popupModel.setActivePopup('media_edit', {
    active: true,
    component: EditMedia,
    props: {
      item: item,
      onSave: model.saveData,
      gallery,
      isMain
    }
  });
};
const Buttons = ({ item, gallery, isMain = false }) => {
  return (
    <View style={{ gap: remCss(10), flexDirection: 'row', height: remCss(30) }}>
      <MCButton
        tabIndex={-1}
        label={LANG_CONST.buttons.edit}
        light
        radius
        small
        primary
        type='button'
        onClick={() =>
          popupModel.setActivePopup('add_profile_gallery', {
            active: true,
            component: AddGallery,
            props: {
              onSave: model.saveData,
              item,
              isMain,
              gallery
            }
          })
        }
      />
      <MCButton
        tabIndex={-1}
        padding={10}
        iconname={BASE_ICONS.CLOSE}
        light
        radius
        small
        secondary
        type='button'
        onClick={(e) => {
          e.preventDefault();
          model.del(item.id, isMain);
        }}
      />
    </View>
  );
};

const FooterItem = ({ item, gallery, editPropsFn, isMain = false, rowIndex, colIndex }) => {
  const [edit, setEdit] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState({});

  const onChange = useCallback(
    (e, name, v) => {
      const target = {
        ...(edit || {}),
        [name]: v
      };
      setEditing(true);
      setInvalid({});
      setEdit(target);
    },
    [edit]
  );

  const onSave = useCallback(() => {
    setLoading(true);
    model.saveData({ ...item, ...edit, isMain }).then((r) => {
      setLoading(false);
      if (r.ok) {
        setEdit(null);
        setEditing(false);
      } else {
        setInvalid(r.invalid);
      }
    });
  }, [edit, isMain, item]);

  return (
    <View style={{ gap: remCss(10), position: 'relative' }}>
      <View style={{ flexDirection: 'row', gap: remCss(10) }}>
        <MCEditText
          {...editPropsFn(rowIndex, colIndex)}
          value={edit?.title !== undefined ? edit?.title : item.title}
          size={20}
          onChange={(e, v) => onChange(e, 'title', v)}
          maxLength={240}
          textLight
          flex
        />
        <Buttons item={item} gallery={gallery} isMain={isMain} />
      </View>
      {invalid?.title && <MCError error={invalid?.title} label={LANG_CONST.title} />}
      <View style={{ flexDirection: 'row', gap: remCss(10), flex: 1 }}>
        <MCEditText
          {...editPropsFn(rowIndex + 1, colIndex)}
          isTextArea={true}
          value={edit?.description !== undefined ? edit?.description : item.description}
          onChange={(e, v) => onChange(e, 'description', v)}
          size={22}
          weight={500}
          maxLength={3000}
          flex
          heading
        />
      </View>
      {invalid?.description && (
        <MCError error={invalid?.description} label={LANG_CONST.description} />
      )}
      <View
        style={{
          flexDirection: 'row',
          position: 'absolute',
          bottom: isMain ? -35 : -70,
          width: `100%`
        }}
      >
        <View style={{ flex: 1, height: remCss(30) }} />
        {editing && (
          <MCButton
            primary
            disabled={loading}
            tabIndex={-1}
            loading={loading}
            style={{ width: remCss(65), height: remCss(30) }}
            label={LANG_CONST.buttons.save}
            dark
            onClick={onSave}
            radius
            tiny
          />
        )}
      </View>
    </View>
  );
};

const Item = ({ item, gallery, row, col, editPropsFn }) => {
  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'row',
        gap: remCss(20),
        marginBottom: remCss(25),
        justifyContent: 'flex-start'
      }}
    >
      <View
        className={'mc-bg-primary-light'}
        style={{
          minWidth: remCss(80),
          height: remCss(80),
          textAlign: 'center',
          justifyContent: 'center',
          borderRadius: '50%'
        }}
      >
        <MCText primary size={18} label={moment(item.date).format('YYYY')} />
        <MCText
          primary
          size={24}
          weight={500}
          label={moment(item.date).format('MMM').toUpperCase()}
        />
      </View>
      <View style={{ flex: 1, gap: remCss(10) }}>
        <View
          style={{
            flexDirection: 'column',
            gap: remCss(20),
            position: 'relative',
            borderColor: scssVariables['colors-mc-border'],
            borderWidth: 1,
            borderRadius: remCss(10),
            borderStyle: 'solid',
            padding: remCss(20)
          }}
        >
          <View
            style={{
              position: 'absolute',
              top: remCss(32),
              left: remCss(-10),
              backgroundColor: 'white'
            }}
          >
            <SVGIcon
              iconname={'arrow-icon'}
              strokeWidth={0.5}
              fill={scssVariables['colors-mc-border2']}
            />
          </View>
          <View style={{ position: 'relative' }}>
            <MediaGroup
              images={item.media || []}
              template={6}
              length={2}
              aspect={3 / 4}
              onViewEdit={() => openMediaEdit(item, null, false)}
            />
            <View
              style={{
                position: 'absolute',
                right: item.media.length > 1 ? remCss(10) : `calc(50% + ${remCss(15)}px)`,
                backgroundColor: 'white',
                borderRadius: remCss(5),
                top: remCss(10)
              }}
            >
              <MCButton
                iconname={BASE_ICONS.EDIT}
                light
                outline
                radius
                primary
                padding={10}
                small
                onClick={() => openMediaEdit(item, null, false)}
              />
            </View>
          </View>
          <FooterItem
            key={
              (item.id || item.date || row) +
              '_' +
              (item.title || '') +
              '_' +
              (item.description || '')
            }
            editPropsFn={editPropsFn}
            item={item}
            gallery={gallery}
            colIndex={col}
            rowIndex={row}
          />
        </View>
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1, height: remCss(30) }} />
        </View>
      </View>
    </View>
  );
};

export const GalleryList = () => {
  model.use();

  const gallery = model.state.gallery;

  const screen = useScreenDetail();

  const items = model.items();
  const hasMain = !!gallery?.main?.title;

  let { total = 0 } = model.getCurrent();

  return (
    <MCBaseEditableTable
      topRowExtend={(editPropsFn) => {
        return (
          <tr style={{ display: 'flex', gridColumn: '1 / -1' }}>
            {hasMain && (
              <td
                colSpan={2}
                style={{
                  flex: 1,
                  marginBottom: remCss(50)
                }}
              >
                <View
                  style={{
                    width: '100%',
                    margin: 'auto',
                    maxWidth: 1550,
                    position: 'relative',
                    gap: remCss(20)
                  }}
                >
                  <View
                    style={{
                      backgroundColor: 'white',
                      borderRadius: remCss(5),
                      position: 'absolute',
                      right: remCss(10),
                      top: remCss(10),
                      zIndex: 1
                    }}
                  >
                    <MCButton
                      iconname={BASE_ICONS.EDIT}
                      light
                      outline
                      primary
                      radius
                      padding={10}
                      small
                      onClick={() => openMediaEdit(gallery.main, gallery, true)}
                    />
                  </View>
                  <Splide>
                    {gallery.main.media.map((m, i) => (
                      <SplideSlide key={i}>
                        <FileBox
                          template={2}
                          file={m}
                          list={gallery.main.media}
                          onClose={null}
                          onViewEdit={() => openMediaEdit(gallery.main, gallery, true)}
                          styles={{
                            width: '100%',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            maxWidth: remCss(835),
                            height: remCss(360)
                          }}
                          videoProps={{
                            width: '100%',
                            height: '100%'
                          }}
                          onlyPreview={true}
                          showFileName={false}
                        />
                      </SplideSlide>
                    ))}
                  </Splide>
                  <FooterItem
                    key={
                      'main_' +
                      (gallery.main?.id || gallery.main?.date || '') +
                      '_' +
                      (gallery.main?.title || '') +
                      '_' +
                      (gallery.main?.description || '')
                    }
                    editPropsFn={editPropsFn}
                    item={gallery.main}
                    gallery={gallery}
                    isMain={true}
                    colIndex={0}
                    rowIndex={0}
                  />
                </View>
              </td>
            )}
          </tr>
        );
      }}
      columns={[
        {
          cellStyles: { alignItems: 'flex-start' },
          renderEditCell: (editPropsFn, item, index) => {
            const firstIndex = index * 2;
            if (!items[firstIndex]) return null;
            return (
              <View
                style={{
                  flexDirection: screen.isLargeScreen ? 'row' : 'column',
                  gap: remCss(20),
                  maxWidth: 1550,
                  marginRight: 'auto',
                  marginLeft: 'auto'
                }}
              >
                <Item
                  editPropsFn={editPropsFn}
                  item={items[firstIndex]}
                  gallery={gallery}
                  row={2 + firstIndex}
                  col={0}
                />
                {!items[firstIndex + 1] ? (
                  <View style={{ flex: 1 }} />
                ) : (
                  <Item
                    editPropsFn={editPropsFn}
                    item={items[firstIndex + 1]}
                    gallery={gallery}
                    row={2 + firstIndex}
                    col={1}
                  />
                )}
              </View>
            );
          }
        }
      ]}
      list={items}
      selectedPage={model.state.pageIndex}
      total={total}
      limit={model.listLimit}
      onSetPage={(v) => {
        model.handleChange('pageIndex', v);
        model.setListState({ items: [] }, model.getCurrentId(), null, true);
        model.loadMore();
      }}
    />
  );
};
